import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useImmer } from "use-immer";

import { IgAccordion, IgDivider, IgTooltip } from "@intelligems/ig-theme";
import {
  MessageBoxWidgetConfig,
  MessageBoxWidgetDto,
  ProgressBarWidgetConfig,
  ProgressBarWidgetDto,
  QuantityButtonsWidgetConfig,
  QuantityButtonsWidgetDto,
} from "@intelligems/ig-types";
import { merge } from "@intelligems/ig-utils";
import { resolveDesktopMessageBoxConfig } from "@intelligems/ig-widgets/MessageBoxView/resolveMessageBoxConfig";
import { resolveProgressBarDesktopConfig } from "@intelligems/ig-widgets/ProgressBarView/resolveProgressBarWidgetConfig";
import { resolveDesktopQuantityButtonsConfig } from "@intelligems/ig-widgets/QuantityButtonsView/resolveQuantityButtonsConfig";
import { Grid, useTheme } from "@mui/material";
import { BarAndBreakpointSection } from "@src/components/ColorAndStyle/sections/BarAndBreakpointSection";
import { RootState, useDispatch } from "@src/store";
import { getVariationOffer } from "@src/store/slices/offers/selectors";
import {
  updateUserInterface,
  useUserInterfaceWithStyles,
} from "@src/store/slices/userInterfaces";
import {
  setWidgetConfig,
  useWidgetWithStyles,
} from "@src/store/slices/widgets";
import {
  isMessageBox,
  isProgressBar,
  isQuantityButtons,
} from "@src/store/slices/widgets/util";

import { useColorPicker } from "./hooks/use-color-picker";
import { AppearanceSection } from "./sections/AppearanceSection";
import { ColorPickerSection } from "./sections/ColorPickerSection";
import { ColorThemeSection } from "./sections/ColorThemeSection";
import { ComponentStyleVariantSelection } from "./sections/ComponentStyleVariantSelection";
import { LayoutSection } from "./sections/LayoutSection";
import { PlacementOnPage } from "./sections/PlacementOnPage";
import { TypographySection } from "./sections/TypographySection";
import {
  createColorChangeHandler,
  createLayoutChangeHandler,
  createProgressBarStyleChangeHandler,
  createStyleChangeHandler,
  createTypographyChangeHandler,
} from "./utils/handlers";
import { updateStyleOverrides } from "./utils/styleOverrides";
import { ColorAndStyleSectionProps } from "./types";

export const ColorAndStyle = ({
  variationId,
  widgetId,
  widgetType,
}: ColorAndStyleSectionProps) => {
  const dispatch = useDispatch();
  const theme = useTheme();

  const offer = useSelector((state: RootState) =>
    variationId ? getVariationOffer(variationId)(state) : undefined
  );
  const currentUserInterface = useUserInterfaceWithStyles(
    variationId || "",
    widgetType
  );
  const currentWidget = useWidgetWithStyles(widgetId || "", widgetType);

  const widget = useMemo(() => {
    if (variationId && currentUserInterface?.widget) {
      return currentUserInterface.widget;
    }
    if (widgetId && currentWidget) {
      return currentWidget;
    }
    return undefined;
  }, [variationId, widgetId, currentUserInterface?.widget, currentWidget]);
  const quantityButtonVariant =
    widgetType === "quantityButtons"
      ? widget?.config?.desktop?.variant
      : undefined;

  const widgetBasedStyleConfig = useMemo(() => {
    let baseConfig: any = {};

    if (widget?.config) {
      switch (widgetType) {
        case "quantityButtons":
          baseConfig = resolveDesktopQuantityButtonsConfig(
            widget.config as QuantityButtonsWidgetConfig
          );
          break;
        case "discountProgressBar":
        case "shippingProgressBar":
          baseConfig = resolveProgressBarDesktopConfig(
            widget.config as ProgressBarWidgetConfig,
            offer
          );
          break;
        case "messageBoxPopup":
        case "messageBoxSlideOut":
          baseConfig = resolveDesktopMessageBoxConfig(
            widget.config as MessageBoxWidgetConfig
          );
          break;
        default:
          baseConfig = {};
      }
    }

    if (widgetId && currentWidget) {
      return baseConfig;
    } else if (currentUserInterface?.styleOverrides) {
      return merge(baseConfig, {
        styles: currentUserInterface.styleOverrides,
      });
    }

    return baseConfig;
  }, [
    widgetType,
    widget?.config,
    currentUserInterface?.styleOverrides,
    widgetId,
    currentWidget,
    offer,
  ]);

  const [styleConfig, setStyleConfig] = useImmer<any>(widgetBasedStyleConfig);
  const { colorPickerState, openColorPicker, closeColorPicker } =
    useColorPicker();

  const updateReduxStyleOverrides = (field: string, value: any) => {
    if (!currentUserInterface) return;

    const updatedStyleOverrides = updateStyleOverrides(
      currentUserInterface.styleOverrides || {},
      field,
      value
    );

    dispatch(
      updateUserInterface({
        id: currentUserInterface.id,
        styleOverrides: updatedStyleOverrides,
      })
    );
  };

  const updateWidgetStyles = (field: string, value: any) => {
    if (!currentWidget || !widgetId) return;
    const stylePath = `styles.${field}` as any;
    dispatch(
      setWidgetConfig({
        device: "desktop",
        key: stylePath,
        value,
      })
    );
  };

  useEffect(() => {
    setStyleConfig(widgetBasedStyleConfig);
  }, [
    currentUserInterface?.id,
    currentWidget?.id,
    widgetType,
    setStyleConfig,
    widgetBasedStyleConfig,
  ]);

  const handlerParams = {
    setStyleConfig,
    updateReduxStyleOverrides: widgetId
      ? updateWidgetStyles
      : updateReduxStyleOverrides,
    widget: widget as
      | ProgressBarWidgetDto
      | QuantityButtonsWidgetDto
      | MessageBoxWidgetDto,
    currentUserInterface: widgetId ? null : currentUserInterface,
    dispatch,
    theme,
  };

  const handleColorChange = createColorChangeHandler(handlerParams);
  const handleTypographyChange = createTypographyChangeHandler(handlerParams);
  const handleStyleChange = createStyleChangeHandler(handlerParams);
  const handleProgressBarStyleChange =
    createProgressBarStyleChangeHandler(handlerParams);
  const handleLayoutChange = createLayoutChangeHandler(handlerParams);
  if (!widget) return null;
  const isDisabled =
    styleConfig?.variant !== "progressBar1" &&
    styleConfig?.variant !== "progressBar2" &&
    styleConfig?.variant !== "progressBar3" &&
    styleConfig?.variant !== "messageBoxPopupWidget1" &&
    styleConfig?.variant !== "messageBoxSlideOutWidget1" &&
    styleConfig?.variant !== "quantityButtonsWidgetClassic" &&
    styleConfig?.variant !== "quantityButtonsWidgetImage" &&
    styleConfig?.variant !== "quantityButtonsWidgetCompact";
  return (
    <IgAccordion defaultExpanded={!!widgetId} title={"Color & Style"}>
      <Grid container direction="column" rowSpacing={5}>
        {widget && currentUserInterface?.id && (
          <ComponentStyleVariantSelection
            interfaceId={currentUserInterface.id}
            widget={widget}
          />
        )}
        <IgTooltip
          title={
            isDisabled
              ? "Update to our new component styles to access this section"
              : ""
          }
        >
          <Grid
            container
            direction="column"
            rowSpacing={5}
            sx={{
              ...(isDisabled && {
                opacity: 0.5,
                marginTop: 2,
              }),
            }}
          >
            {isQuantityButtons(widget) ? (
              <AppearanceSection
                cornerRadius={styleConfig.styles?.appearance?.cornerRadius}
                spacing={styleConfig.styles?.appearance?.spacing}
                onCornerRadiusChange={(value) =>
                  handleStyleChange("cornerRadius", value)
                }
                disabled={isDisabled}
                onSpacingChange={(value) => handleStyleChange("spacing", value)}
              />
            ) : isMessageBox(widget) ? (
              <AppearanceSection
                disabled={isDisabled}
                cornerRadius={
                  parseInt(styleConfig.styles?.container?.cornerRadius) || 8
                }
                onCornerRadiusChange={(value) =>
                  handleStyleChange("cornerRadius", value)
                }
              />
            ) : (
              <>
                <Grid item>
                  <IgDivider />
                </Grid>

                <BarAndBreakpointSection
                  disabled={isDisabled}
                  bar={styleConfig.styles?.bar}
                  breakpoint={styleConfig.styles?.breakpoint}
                  onChange={handleProgressBarStyleChange}
                  showBreakpointOptions={
                    styleConfig?.variant === "progressBar1" ||
                    styleConfig?.variant === "progressBar2"
                  }
                />
              </>
            )}
            <Grid item>
              <IgDivider />
            </Grid>
            {isMessageBox(widget) ? (
              <PlacementOnPage
                disabled={isDisabled}
                placement={styleConfig.styles?.placement || "top-left"}
                onPlacementChange={(value) =>
                  handleStyleChange("placement", value)
                }
              />
            ) : (
              <LayoutSection
                disabled={isDisabled}
                widget={widget}
                widgetType={widgetType}
                layout={
                  isProgressBar(widget)
                    ? styleConfig.styles?.layout?.swapTextPositions
                    : styleConfig.styles?.appearance?.layout
                }
                onLayoutChange={handleLayoutChange}
              />
            )}

            <Grid item>
              <IgDivider />
            </Grid>

            <ColorThemeSection
              disabled={isDisabled}
              widgetType={widgetType}
              colorTheme={styleConfig.styles}
              onColorChange={(field, event) => {
                if (!isDisabled) {
                  openColorPicker(field, event);
                }
              }}
              offer={offer}
              widget={widget}
              quantityButtonVariant={quantityButtonVariant}
            />

            <Grid item>
              <IgDivider />
            </Grid>

            <TypographySection
              disabled={isDisabled}
              widget={widget}
              typography={styleConfig.styles?.typography}
              onTypographyChange={handleTypographyChange}
              offer={offer}
              quantityButtonVariant={quantityButtonVariant}
            />
          </Grid>
        </IgTooltip>
      </Grid>

      <ColorPickerSection
        colorPickerState={colorPickerState}
        onClose={closeColorPicker}
        onColorChange={handleColorChange}
        styleConfig={styleConfig}
        widget={widget}
      />
    </IgAccordion>
  );
};
